import React, { createContext, useContext, useMemo } from 'react';

type SecurityTier = 1 | 2 | 3;
type UpgradeAction = 'enable-2fa' | 'setup-api-key' | null;

interface SecurityTierContextValue {
  tier: SecurityTier;
  canAccess: (requiredTier: SecurityTier) => boolean;
  nextUpgradeAction: UpgradeAction;
}

interface SecurityTierUser {
  is2faEnabled: boolean;
  hasVerifiedApiKey: boolean;
}

interface SecurityTierProviderProps {
  user: SecurityTierUser;
  children: React.ReactNode;
}

const SecurityTierContext = createContext<SecurityTierContextValue | undefined>(undefined);

function computeTier(user: SecurityTierUser): SecurityTier {
  if (user.is2faEnabled && user.hasVerifiedApiKey) return 3;
  if (user.is2faEnabled) return 2;
  return 1;
}

function computeNextAction(tier: SecurityTier): UpgradeAction {
  if (tier === 1) return 'enable-2fa';
  if (tier === 2) return 'setup-api-key';
  return null;
}

export const SecurityTierProvider: React.FC<SecurityTierProviderProps> = ({ user, children }) => {
  const value = useMemo(() => {
    const tier = computeTier(user);
    return {
      tier,
      canAccess: (requiredTier: SecurityTier) => tier >= requiredTier,
      nextUpgradeAction: computeNextAction(tier),
    };
  }, [user.is2faEnabled, user.hasVerifiedApiKey]);

  return (
    <SecurityTierContext.Provider value={value}>
      {children}
    </SecurityTierContext.Provider>
  );
};

export function useSecurityTier(): SecurityTierContextValue {
  const context = useContext(SecurityTierContext);
  if (!context) {
    throw new Error('useSecurityTier must be used within SecurityTierProvider');
  }
  return context;
}
